<template>
  <div class="page boss-page task-planning-page">
    <van-nav-bar title="任务拆分与分配" left-arrow fixed placeholder @click-left="$router.back()"/>

    <section class="progress">
      <span :class="{done: true}">1 创建项目</span>
      <span :class="{done: items.length}">2 AI拆任务</span>
      <span :class="{done: approvedCount}">3 人工复审</span>
      <span :class="{done: assignedCount}">4 分配员工</span>
    </section>

    <section class="toolbar panel">
      <h2>{{ items.length ? '任务草案' : '下一步：拆分任务' }}</h2>
      <p>AI 只生成初稿，Boss 或主管可反复修改复审，主管最终人工分配。</p>
      <van-button block type="danger" class="gen-btn" icon="cluster-o" :loading="generating" @click="generate">
        {{ items.length ? '重新生成 AI 草案' : 'AI 初步拆分任务' }}
      </van-button>
    </section>

    <section v-if="selectable.length" class="batch panel">
      <label class="check-all">
        <input type="checkbox" :checked="allSelected" @change="toggleAll"> 全选待处理
      </label>
      <div class="batch-row">
        <input v-model="batchDueAt" type="date" class="date-input">
        <van-button class="batch-btn" plain type="danger" @click="batchApprove">批量通过</van-button>
        <van-button class="batch-btn" type="danger" @click="batchAssign">批量分配</van-button>
      </div>
    </section>

    <section v-if="approvedItems.length" class="filter panel">
      <b>分配范围</b>
      <select v-model="department" class="dept-select">
        <option value="">全部部门</option>
        <option v-for="d in departments" :key="d">{{ d }}</option>
      </select>
      <small>选择部门后，员工列表会自动缩小</small>
    </section>

    <section v-for="(item, index) in items" :key="field(item,'id')" class="item panel">
      <header>
        <input v-if="['AI_DRAFT','APPROVED','REJECTED'].includes(status(item))" v-model="checked" type="checkbox" :value="field(item,'id')" class="item-check">
        <i class="idx">{{ index + 1 }}</i>
        <span class="title-wrap">
          <b>{{ field(item, 'title') }}</b>
          <em :class="status(item).toLowerCase()">{{ statusText(status(item)) }}</em>
        </span>
      </header>
      <dl>
        <dt>任务目标</dt>
        <dd>{{ field(item, 'objective') }}</dd>
        <dt>验收标准</dt>
        <dd>{{ field(item, 'acceptance_criteria') }}</dd>
      </dl>
      <div v-if="['AI_DRAFT','REJECTED'].includes(status(item))" class="item-actions">
        <van-button class="action-btn" plain type="danger" @click="review(item,'REJECT')">不采用</van-button>
        <van-button class="action-btn" type="danger" @click="editReview(item)">修改并通过</van-button>
      </div>
      <div v-else-if="status(item)==='APPROVED'" class="assign">
        <select v-model="selected[field(item,'id')]" class="emp-select">
          <option value="">选择执行员工</option>
          <option v-for="e in filteredCandidates" :key="e.employeeId" :value="e.employeeId">{{ e.employeeName }} · {{ e.positionName }}</option>
        </select>
        <div class="assign-btns">
          <van-button class="action-btn" plain type="danger" @click="editReview(item)">再修改</van-button>
          <van-button class="action-btn" type="danger" @click="assign(item)">确认分配</van-button>
        </div>
      </div>
      <div v-else-if="status(item)==='ASSIGNED'" class="assigned">
        <van-icon name="checked"/>
        已分配：{{ field(item, 'proposed_assignee_name') || field(item, 'proposed_assignee_id') }}
      </div>
    </section>

    <van-empty v-if="!items.length" class="empty" description="暂无草案，点击 AI 初步拆分任务开始"/>

    <van-dialog v-model="showEdit" title="人工复审任务" show-cancel-button confirm-button-text="修改并通过" @confirm="confirmReview">
      <van-field v-model="form.title" required label="任务名称"/>
      <van-field v-model="form.objective" required type="textarea" rows="2" autosize label="任务目标"/>
      <van-field v-model="form.acceptanceCriteria" required type="textarea" rows="2" autosize label="验收标准"/>
      <van-field v-model="form.dueAt" type="date" label="截止日期"/>
    </van-dialog>
  </div>
</template>
<script>
import api from '@/utils/coreapi';

export default {
  data() {
    return {
      items: [],
      candidates: [],
      selected: {},
      checked: [],
      batchDueAt: '',
      department: '',
      generating: false,
      showEdit: false,
      current: null,
      form: {}
    };
  },
  computed: {
    selectable() {
      return this.items.filter(x => ['AI_DRAFT', 'APPROVED', 'REJECTED'].includes(this.status(x)));
    },
    allSelected() {
      return this.selectable.length > 0 && this.selectable.every(x => this.checked.map(Number).includes(Number(this.field(x, 'id'))));
    },
    approvedItems() {
      return this.items.filter(x => this.status(x) === 'APPROVED');
    },
    approvedCount() {
      return this.items.filter(x => ['APPROVED', 'ASSIGNED'].includes(this.status(x))).length;
    },
    assignedCount() {
      return this.items.filter(x => this.status(x) === 'ASSIGNED').length;
    },
    departments() {
      return [...new Set(this.candidates.map(x => x.departmentName).filter(Boolean))];
    },
    filteredCandidates() {
      return this.department ? this.candidates.filter(x => x.departmentName === this.department) : this.candidates;
    }
  },
  mounted() {
    this.load();
    if (this.$route.query.newProject === '1') this.$toast('项目已创建，请让 AI 拆分任务');
  },
  methods: {
    field(x, k) {
      return x[k] !== undefined ? x[k] : x[k.toUpperCase()];
    },
    status(x) {
      return String(this.field(x, 'status') || '');
    },
    statusText(v) {
      return ({AI_DRAFT: '待复审', APPROVED: '待分配', REJECTED: '已驳回', ASSIGNED: '已分配'})[v] || v;
    },
    toggleAll(e) {
      this.checked = e.target.checked ? this.selectable.map(x => this.field(x, 'id')) : [];
    },
    async load() {
      try {
        const id = this.$route.params.id;
        this.items = await api.projectTaskPlans(id) || [];
        this.candidates = await api.projectTaskCandidates(id) || [];
        this.checked = [];
        if (this.departments.length === 1) this.department = this.departments[0];
      } catch (e) {
        this.$toast.fail(e.message);
      }
    },
    async generate() {
      this.generating = true;
      try {
        this.items = await api.generateProjectTaskPlans(this.$route.params.id);
        this.$toast.success('草案已生成，请人工复审');
      } catch (e) {
        this.$toast.fail(e.message);
      } finally {
        this.generating = false;
      }
    },
    async batchApprove() {
      if (!this.checked.length) return this.$toast('请选择任务草案');
      try {
        await api.batchReviewProjectTaskPlans(this.$route.params.id, {
          proposalIds: this.checked,
          action: 'APPROVE',
          dueAt: this.batchDueAt || null
        });
        await this.load();
        this.$toast.success('已批量复审通过');
      } catch (e) {
        this.$toast.fail(e.message);
      }
    },
    async batchAssign() {
      const assignments = this.checked.map(id => ({proposalId: Number(id), employeeId: Number(this.selected[id] || 0)}));
      if (!assignments.length || assignments.some(x => !x.employeeId)) return this.$toast('请为所选任务逐一选择执行员工');
      try {
        await api.batchAssignProjectTaskPlans(this.$route.params.id, {assignments});
        await this.load();
        this.$toast.success('已批量人工分配');
      } catch (e) {
        this.$toast.fail(e.message);
      }
    },
    editReview(item) {
      this.current = item;
      this.form = {
        title: this.field(item, 'title'),
        objective: this.field(item, 'objective'),
        acceptanceCriteria: this.field(item, 'acceptance_criteria'),
        dueAt: (this.field(item, 'due_at') || '').slice(0, 10)
      };
      this.showEdit = true;
    },
    async confirmReview() {
      if (!this.form.title || !this.form.objective || !this.form.acceptanceCriteria) return this.$toast('请补全任务和验收标准');
      try {
        await api.reviewProjectTaskPlan(this.$route.params.id, this.field(this.current, 'id'), {...this.form, action: 'APPROVE'});
        await this.load();
        this.$toast.success('已通过，请分配员工');
      } catch (e) {
        this.$toast.fail(e.message);
      }
    },
    async review(item, action) {
      try {
        await api.reviewProjectTaskPlan(this.$route.params.id, this.field(item, 'id'), {action});
        await this.load();
      } catch (e) {
        this.$toast.fail(e.message);
      }
    },
    async assign(item) {
      const employeeId = this.selected[this.field(item, 'id')];
      if (!employeeId) return this.$toast('请选择员工');
      try {
        await api.assignProjectTaskPlan(this.$route.params.id, this.field(item, 'id'), Number(employeeId));
        await this.load();
        this.$toast.success('任务已下发，员工可生成人工确认的工作要点');
      } catch (e) {
        this.$toast.fail(e.message);
      }
    }
  }
};
</script>
<style lang="less" scoped>
@import './boss.less';

.task-planning-page {
  min-height: 100vh;
  margin: 0;
  padding-bottom: 1.2rem;
  background: #f6f7fb;
  color: #30313a;
}

.task-planning-page > .van-nav-bar.van-nav-bar--fixed,
.task-planning-page > .van-nav-bar,
.task-planning-page /deep/ .van-nav-bar {
  top: 0 !important;
  left: 0 !important;
  margin: 0 !important;
  width: 100vw !important;
  max-width: 100% !important;
  border-radius: 0 !important;
}

.progress {
  margin: .2rem .28rem 0;
  padding: .14rem;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: .08rem;
  background: #fff;
  border-radius: .16rem;
  box-shadow: 0 .05rem .2rem rgba(30, 35, 60, .06);
}

.progress span {
  padding: .12rem .04rem;
  text-align: center;
  color: #9295a2;
  font-size: .2rem;
  line-height: 1.35;
  border-radius: .08rem;
  background: #f0f1f4;
}

.progress .done {
  color: #fff;
  background: linear-gradient(135deg, #d32f2f, #b71c1c);
}

.panel {
  margin: .2rem .28rem 0;
  padding: .24rem .26rem;
  background: #fff;
  border-radius: .16rem;
  box-shadow: 0 .05rem .2rem rgba(30, 35, 60, .06);
}

.toolbar h2 {
  margin: 0;
  font-size: .32rem;
  color: #30313a;
}

.toolbar p {
  margin: .08rem 0 0;
  color: #888;
  font-size: .23rem;
  line-height: 1.5;
}

.gen-btn {
  margin-top: .2rem;
  height: .88rem;
  border-radius: .12rem;
  font-size: .28rem;
  font-weight: 500;
}

.batch .check-all {
  display: flex;
  align-items: center;
  gap: .1rem;
  font-size: .26rem;
  color: #34364a;
}

.batch-row {
  margin-top: .14rem;
  display: flex;
  flex-wrap: wrap;
  gap: .1rem;
  align-items: center;
}

.batch-btn {
  flex: 1;
  min-width: 1.6rem;
  height: .72rem !important;
  padding: 0 .18rem !important;
  font-size: .24rem !important;
  border-radius: .1rem !important;
}

.batch-btn.van-button--plain.van-button--danger {
  color: #c62828 !important;
  border-color: #efb8b8 !important;
  background: #fff5f5 !important;
}

.batch-btn.van-button--danger:not(.van-button--plain) {
  border: 0 !important;
  background: linear-gradient(135deg, #d32f2f, #b71c1c) !important;
  box-shadow: 0 .04rem .1rem rgba(198, 40, 40, .2);
}

.date-input,
.dept-select,
.emp-select {
  height: .72rem;
  padding: 0 .14rem;
  color: #34364a;
  font-size: .24rem;
  border: 1PX solid #e8e8ec;
  border-radius: .1rem;
  background: #fafafa;
}

.date-input {
  flex: 1;
  min-width: 2.4rem;
}

.filter {
  display: grid;
  grid-template-columns: 1.3rem 1fr;
  align-items: center;
  gap: .1rem;
}

.filter b {
  font-size: .27rem;
  color: #34364a;
}

.filter small {
  grid-column: 2;
  color: #999;
  font-size: .21rem;
}

.item-actions,
.assign-btns {
  display: flex;
  gap: .12rem;
}

.item-actions .action-btn,
.assign-btns .action-btn {
  flex: 1;
  min-width: 0;
  height: .76rem !important;
  padding: 0 .16rem !important;
  font-size: .24rem !important;
  line-height: 1.2 !important;
  border-radius: .1rem !important;
  white-space: nowrap;
}

.item-actions .action-btn.van-button--plain.van-button--danger,
.assign-btns .action-btn.van-button--plain.van-button--danger {
  color: #c62828 !important;
  border-color: #efb8b8 !important;
  background: #fff5f5 !important;
}

.item-actions .action-btn.van-button--danger:not(.van-button--plain),
.assign-btns .action-btn.van-button--danger:not(.van-button--plain) {
  border: 0 !important;
  color: #fff !important;
  background: linear-gradient(135deg, #d32f2f, #b71c1c) !important;
  box-shadow: 0 .04rem .1rem rgba(198, 40, 40, .2);
}

.item header {
  display: flex;
  align-items: flex-start;
  gap: .12rem;
}

.item-check {
  margin-top: .12rem;
}

.idx {
  width: .48rem;
  height: .48rem;
  line-height: .48rem;
  text-align: center;
  color: #fff;
  font-size: .22rem;
  font-style: normal;
  border-radius: 50%;
  background: #c62828;
  flex: 0 0 .48rem;
}

.title-wrap {
  min-width: 0;
  flex: 1;
}

.title-wrap b {
  display: block;
  font-size: .3rem;
  color: #34364a;
  line-height: 1.4;
}

.title-wrap em {
  display: inline-block;
  margin-top: .06rem;
  padding: .04rem .12rem;
  color: #ad6a14;
  font-size: .21rem;
  font-style: normal;
  border-radius: .2rem;
  background: #fff3df;
}

.title-wrap em.approved {
  color: #c62828;
  background: #fff0f0;
}

.title-wrap em.assigned {
  color: #278e56;
  background: #eaf8f0;
}

.title-wrap em.rejected {
  color: #888;
  background: #f0f0f0;
}

dl {
  margin: .18rem 0 0;
}

dt {
  color: #9295a2;
  font-size: .22rem;
}

dd {
  margin: .04rem 0 .14rem;
  font-size: .26rem;
  line-height: 1.5;
  color: #555;
}

.assign {
  margin-top: .14rem;
}

.emp-select {
  width: 100%;
  margin-bottom: .12rem;
}

.assigned {
  margin-top: .14rem;
  padding: .16rem .18rem;
  display: flex;
  align-items: center;
  gap: .08rem;
  color: #278e56;
  font-size: .25rem;
  background: #eef9f3;
  border-radius: .1rem;
}

.empty {
  margin-top: .4rem;
}
</style>



// WEBPACK FOOTER //
// src/views/boss/ProjectTaskPlanning.vue